import { createContext, useContext, useState } from 'react'
import type { ReactNode } from 'react'
import type { Product } from '@/shared/types'

export type AdjustmentLine = {
  product: Product
  qty: number
}

const CODE_CHARS = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'

export function generateAdjustmentCode(): string {
  return Array.from(
    { length: 6 },
    () => CODE_CHARS[Math.floor(Math.random() * CODE_CHARS.length)]
  ).join('')
}

type AdjustmentContextValue = {
  lines: AdjustmentLine[]
  setLines: React.Dispatch<React.SetStateAction<AdjustmentLine[]>>
  adjustmentCode: string
  direction: 'increase' | 'decrease'
  setDirection: React.Dispatch<React.SetStateAction<'increase' | 'decrease'>>
  reason: string
  setReason: React.Dispatch<React.SetStateAction<string>>
  resetAdjustment: () => void
}

const AdjustmentContext = createContext<AdjustmentContextValue | null>(null)

export function AdjustmentProvider({ children }: { children: ReactNode }) {
  const [lines, setLines] = useState<AdjustmentLine[]>([])
  const [adjustmentCode, setAdjustmentCode] = useState(generateAdjustmentCode)
  const [direction, setDirection] = useState<'increase' | 'decrease'>('decrease')
  const [reason, setReason] = useState<string>('expired')

  function resetAdjustment() {
    setLines([])
    setReason('spoilage')
    setDirection('decrease')
    setAdjustmentCode(generateAdjustmentCode())
  }

  return (
    <AdjustmentContext.Provider value={{ lines, setLines, adjustmentCode, direction, setDirection, reason, setReason, resetAdjustment }}>
      {children}
    </AdjustmentContext.Provider>
  )
}

export function useAdjustmentContext() {
  const ctx = useContext(AdjustmentContext)
  if (!ctx) throw new Error('useAdjustmentContext must be used within AdjustmentProvider')
  return ctx
}
